import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, BadgeCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { CaseIntelligence } from '@/pages/anatel/CaseIntelligence';
import { fetchProcess } from '@/services/anatel';
import type { PairingReach, ProcessDecision } from '@/services/types';

export function AnatelProcessDetailPage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const { key = '' } = useParams();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['anatel', 'process', key],
    queryFn: () => fetchProcess(key),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={3} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }

  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4 p-6">
        <BackLink />
        <ErrorMessage
          message={notFound ? t('anatel:process.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  const currency = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });

  return (
    <div className="space-y-4 p-6">
      <BackLink />

      <header className="space-y-1">
        <h1 className="text-lg font-semibold tabular-nums text-foreground">
          {data.process_number}
        </h1>
        <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          {data.entity_name ?? (
            <span className="italic">{t('anatel:decisions.noEntity')}</span>
          )}
          {data.entity_cnpj ? <span className="tabular-nums">· {data.entity_cnpj}</span> : null}
          {data.entity_cnpj_valid ? (
            <span
              className="inline-flex items-center gap-1 text-xs"
              title={t('anatel:process.cnpjValid')}
            >
              <BadgeCheck className="h-3.5 w-3.5" aria-hidden />
              {t('anatel:process.cnpjValid')}
            </span>
          ) : null}
        </p>
      </header>

      <ExplainerCard i18nKey="anatel:process.explainer" />

      <FieldGuide i18nKey="anatel:process.guide" level={2} />

      <AsOfStrip asOf={data.as_of} />

      <ol className="space-y-3">
        {data.decisions.map((decision, index) => (
          <li key={`${decision.decision_number ?? index}`}>
            <DecisionCard decision={decision} currency={currency} pairing={data.pairing} />
          </li>
        ))}
      </ol>
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation(['anatel']);
  return (
    <Link
      to="/anatel/decisions"
      className="inline-flex items-center gap-1.5 text-sm text-primary underline-offset-4 hover:underline"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {t('anatel:process.backToList')}
    </Link>
  );
}

function DecisionCard({
  decision,
  currency,
  pairing,
}: {
  decision: ProcessDecision;
  currency: Intl.NumberFormat;
  /** The corpus's pairing reach, one fact per detail payload, quoted by the
      "not paired" caveat on every card rather than a figure written into a
      locale file. */
  pairing: PairingReach;
}) {
  const { t } = useTranslation(['anatel']);

  return (
    <article className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">
          {decision.decision_number ?? decision.decision_type ?? '—'}
        </h2>
        <span className="text-xs tabular-nums text-muted-foreground">
          {decision.reference_date ?? '—'}
          {/* "Observed", not "final": finality cannot be proven from this data,
              only recency. The label must not claim more than the flag means. */}
          {decision.is_final_observed ? ` · ${t('anatel:process.latestObserved')}` : ''}
        </span>
      </div>

      {decision.deciding_authority ? (
        /* Labelled and disclosed. The raw string reads like a person -- "O
           SUPERINTENDENTE DE CONTROLE DE OBRIGAÇÕES ..." -- but every PADO value
           is an organisational unit, and the 227 distinct strings are further
           inflated by a leading article, so one office appears as two. No
           decision-maker analysis is built on it; the screen has to say why. */
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">{t('anatel:process.authority')}:</span>{' '}
          {decision.deciding_authority}{' '}
          <span className="italic">({t('anatel:process.authorityOffice')})</span>
        </p>
      ) : null}

      <div className="flex flex-wrap gap-1.5">
        {(decision.outcome_codes.length === 0 ? ['UNCLASSIFIED'] : decision.outcome_codes).map(
          (code) => (
            <span
              key={code}
              className="rounded border px-2 py-0.5 text-xs text-muted-foreground"
            >
              {t(`anatel:overview.outcomes.${code}`, { defaultValue: code })}
            </span>
          ),
        )}
        {decision.sanctions
          .filter((sanction) => sanction.amount_brl !== null)
          .map((sanction) => (
            <span
              key={sanction.sanction_type}
              className="rounded border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs tabular-nums text-foreground"
            >
              {currency.format(sanction.amount_brl as number)}
            </span>
          ))}
      </div>

      {decision.legal_citations.length > 0 ? (
        <section className="space-y-1">
          <h3 className="text-xs font-medium text-foreground">
            {t('anatel:process.citations')}
          </h3>
          <ul className="flex flex-wrap gap-1.5">
            {decision.legal_citations.map((citation) => (
              <li
                key={citation}
                className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground"
              >
                {citation}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Null, not empty: a decision with no propositions AND no comparator pair
          gets no panel at all, which is the common case. An empty panel would
          read as "we looked and found nothing" rather than "this decision was
          never paired". */}
      {decision.intelligence ? (
        <CaseIntelligence intelligence={decision.intelligence} pairing={pairing} />
      ) : null}

      {decision.reasoning_subject || decision.reasoning_analysis ? (
        <details className="group space-y-2">
          <summary className="cursor-pointer text-xs font-medium text-primary">
            {t('anatel:process.reasoning')}
          </summary>
          <div className="mt-2 space-y-2 border-l-2 pl-3 text-xs text-muted-foreground">
            {decision.reasoning_subject ? (
              <p className="font-medium text-foreground">{decision.reasoning_subject}</p>
            ) : null}
            {decision.reasoning_analysis ? (
              <p className="whitespace-pre-wrap">{decision.reasoning_analysis}</p>
            ) : null}
            {decision.reasoning_conclusion ? (
              <p className="whitespace-pre-wrap font-medium text-foreground">
                {decision.reasoning_conclusion}
              </p>
            ) : null}
          </div>
        </details>
      ) : null}
    </article>
  );
}
